// AppShell — persistent app chrome for every authenticated page.
// Top bar (brand + tenant + company switcher + search trigger + avatar)
// + Section tabs (Vouchers / Reports / Masters / Banking / Admin)
// + Children content slot.
//
// Replaces the per-page bespoke headers that were leaving users orphaned —
// before this, the only way "out" of any page was the back-link in its header.
// Now the section tabs are always visible, the active section is highlighted
// in Cobalt, and global controls (company, search, avatar) never disappear.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { CompanySwitcher } from '@/components/CompanySwitcher';
import { KbdChip } from '@/components/primitives/KbdChip';
import { fiscalYearForDate } from '@/utils/format';
import { cn } from '@/lib/cn';

interface SectionTab {
  id: string;
  label: string;
  href: string;
  /** Optional sub-paths that should highlight this tab too. */
  match?: (path: string) => boolean;
}

const SECTIONS: SectionTab[] = [
  { id: 'home', label: 'Home', href: '/', match: (p) => p === '/' || p === '' },
  { id: 'vouchers', label: 'Vouchers', href: '/vouchers', match: (p) => p === '/vouchers' || p.startsWith('/vouchers/') },
  { id: 'reports', label: 'Reports', href: '/reports', match: (p) => p === '/reports' || p.startsWith('/reports/') || p.startsWith('/reports-catalogue') },
  { id: 'bi', label: 'BI', href: '/bi', match: (p) => p === '/bi' || p.startsWith('/bi/') },
  { id: 'masters', label: 'Masters', href: '/masters', match: (p) => p === '/masters' || p.startsWith('/masters/') },
  { id: 'banking', label: 'Banking', href: '/banking', match: (p) => p === '/banking' || p.startsWith('/banking/') },
  { id: 'admin', label: 'Admin', href: '/admin', match: (p) => p === '/admin' || p.startsWith('/admin/') },
];

/**
 * Turn a URL path into a breadcrumb trail. e.g.
 *   /admin/audit-log/  →  [{label:'Admin', href:'/admin'}, {label:'Audit log'}]
 */
function computeBreadcrumbs(path: string): { label: string; href?: string }[] {
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 0) return [];
  const trail: { label: string; href?: string }[] = [];
  let acc = '';
  parts.forEach((seg, i) => {
    acc += `/${seg}`;
    const label = seg
      .replace(/-/g, ' ')
      .replace(/^\w/, (c) => c.toUpperCase());
    trail.push({ label, href: i < parts.length - 1 ? acc : undefined });
  });
  return trail;
}

interface Props {
  children: ReactNode;
  /** Optional in-page page header (title + actions). AppShell does NOT render the page title itself. */
  pageHeader?: ReactNode;
  /** Hide the section tab row (e.g. for full-bleed pages like vouchers/new/*). */
  hideTabs?: boolean;
}

export function AppShell({ children, pageHeader, hideTabs }: Props) {
  const tenant = useAuthStore((s) => s.tenant);
  const today = new Date();
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const activeSection = SECTIONS.find((s) => s.match?.(path))?.id ?? null;
  const crumbs = computeBreadcrumbs(path);
  const canGoBack = path !== '/' && crumbs.length > 0;

  function goBack() {
    if (typeof window === 'undefined') return;
    if (window.history.length > 1) window.history.back();
    else window.location.href = '/';
  }

  return (
    <div className="min-h-screen bg-snow">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-mist bg-snow/95 backdrop-blur print:hidden">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center gap-3 px-4 sm:gap-4 sm:px-6">
          <a href="/" className="flex shrink-0 items-center gap-2" aria-label="Orenza home">
            <img src="/brand/mark.svg" alt="" aria-hidden className="h-4 w-4" />
            <span className="font-display text-body-lg font-semibold text-obsidian" style={{ letterSpacing: 'var(--tracking-display)' }}>
              Orenza
            </span>
          </a>

          <div className="hidden h-5 w-px shrink-0 bg-mist md:block" />
          <div className="hidden min-w-0 items-center gap-2 text-body-sm text-slate md:flex">
            <span className="truncate font-medium text-obsidian">{tenant?.name ?? 'Workspace'}</span>
            <span className="text-cool">·</span>
            <span data-numeric className="font-mono text-caption text-slate">{fiscalYearForDate(today)}</span>
          </div>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <div className="shrink-0"><CompanySwitcher /></div>

            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('orenza:open-palette'))}
              className="hidden items-center gap-2 rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-cool transition hover:border-cobalt hover:text-cobalt lg:flex"
              title="Search vouchers, parties, items, screens (Ctrl K)"
            >
              <span>Search…</span>
              <KbdChip>Ctrl K</KbdChip>
            </button>

            <AvatarMenu />
          </div>
        </div>

        {/* Section tabs — persistent IA */}
        {!hideTabs && (
          <nav aria-label="Sections" className="mx-auto flex max-w-[1400px] items-center gap-1 px-4 sm:px-6 -mt-px">
            <div className="flex items-center gap-1 overflow-x-auto py-0">
              {SECTIONS.map((s) => {
                const isActive = activeSection === s.id;
                return (
                  <a
                    key={s.id}
                    href={s.href}
                    className={cn(
                      'inline-flex h-9 items-center border-b-2 px-3 text-body-sm transition',
                      isActive
                        ? 'border-cobalt text-cobalt font-medium'
                        : 'border-transparent text-slate hover:text-obsidian',
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {s.label}
                  </a>
                );
              })}
            </div>
          </nav>
        )}
      </header>

      {/* Breadcrumbs + Back button — global secondary nav */}
      {canGoBack && (
        <div className="border-b border-mist/60 bg-snow/60 print:hidden">
          <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-4 py-2 sm:px-6">
            <button
              type="button"
              onClick={goBack}
              className="inline-flex items-center gap-1 rounded-md border border-mist bg-haze px-2.5 py-1 text-caption font-medium text-slate hover:border-cobalt hover:text-cobalt transition"
              title="Go back (Alt+←)"
              aria-label="Go back"
            >
              <span aria-hidden>←</span>
              <span>Back</span>
            </button>
            <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-caption text-cool overflow-x-auto">
              <a href="/" className="hover:text-cobalt transition shrink-0">Home</a>
              {crumbs.map((c, i) => (
                <span key={i} className="flex items-center gap-1.5 shrink-0">
                  <span className="text-mist" aria-hidden>/</span>
                  {c.href
                    ? <a href={c.href} className="hover:text-cobalt transition">{c.label}</a>
                    : <span className="text-obsidian font-medium">{c.label}</span>}
                </span>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Page header slot (page title + page-specific actions) */}
      {pageHeader && (
        <div className="border-b border-mist bg-snow/70 print:hidden">
          <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6">{pageHeader}</div>
        </div>
      )}

      {/* Main content */}
      <main className="orz-enter mx-auto max-w-[1400px] px-4 py-6 sm:px-6 sm:py-8">{children}</main>
    </div>
  );
}

function AvatarMenu() {
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  async function signOut() {
    try { await api.post('/auth/logout', {}); } catch { /* clear local state anyway */ }
    qc.clear();
    useAuthStore.getState().setUnauthenticated();
    window.location.href = '/';
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-haze text-obsidian font-medium transition hover:bg-mist hover:ring-2 hover:ring-cobalt/40"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${user?.name ?? 'You'} — menu`}
        title={user?.name ?? 'Account menu'}
      >
        {user?.name?.[0]?.toUpperCase() ?? '·'}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-[calc(100%+8px)] z-50 w-64 overflow-hidden rounded-md border border-mist bg-snow shadow-card">
          <div className="border-b border-mist bg-haze/70 px-3 py-2">
            <div className="text-body-sm font-medium text-obsidian truncate">{user?.name ?? '—'}</div>
            <div className="text-caption text-cool truncate">{user?.email ?? ''}</div>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="inline-flex items-center h-4 px-1.5 rounded-sm bg-cobalt/10 text-cobalt text-nano font-medium uppercase tracking-[0.1em]">{user?.role ?? '—'}</span>
              {tenant?.name && <span className="truncate text-nano text-cool">· {tenant.name}</span>}
            </div>
          </div>
          <a href="/profile" role="menuitem" className="flex w-full items-center gap-2 px-3 py-2 text-left text-body-sm text-obsidian hover:bg-haze">Profile</a>
          <a href="/help" role="menuitem" className="flex w-full items-center gap-2 px-3 py-2 text-left text-body-sm text-obsidian hover:bg-haze">Keyboard help</a>
          <a href="/admin" role="menuitem" className="flex w-full items-center gap-2 px-3 py-2 text-left text-body-sm text-obsidian hover:bg-haze">Tenant settings</a>
          <button type="button" role="menuitem" onClick={signOut} className="flex w-full items-center gap-2 border-t border-mist px-3 py-2 text-left text-body-sm text-crimson hover:bg-crimson/[0.05]">
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
